import './i18n'; // Import at top
import { useTranslation } from 'react-i18next';
import React, { useState, useEffect } from 'react';
import { motion, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import './App.css';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { initializeEmailService } from './services/emailService';
import Contact from './components/sections/Contact/Contact';
import Portfolio from './components/sections/Portfolio/Portfolio';
import Header from './components/common/Header/Header';
import About from './components/sections/About/About';

function App() {
  const { t, i18n } = useTranslation();
  const [activeSection, setActiveSection] = useState('home');
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { scrollYProgress } = useScroll();
  const opacity = useTransform(scrollYProgress, [0, 0.2], [1, 0]);

  // Set document direction and language when i18n changes
  useEffect(() => {
    const dir = i18n.language === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.dir = dir;
    document.documentElement.lang = i18n.language;
  }, [i18n.language]);

  useEffect(() => {
    const handleScroll = () => {
      const sections = ['home', 'services', 'about', 'portfolio', 'contact'];
      const current = sections.find(section => {
        const element = document.getElementById(section);
        if (element) {
          const rect = element.getBoundingClientRect();
          return rect.top <= 100 && rect.bottom >= 100;
        }
        return false;
      });
      if (current) setActiveSection(current);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Get services data from translations
  const services = t('services.items', { returnObjects: true }) || [
    {
      icon: '🎨',
      title: 'Branding',
      description: 'Crafting distinctive brand identities that resonate with your audience and stand out in the market.',
      gradient: 'linear-gradient(135deg, #ff4c4c 0%, #ff7676 100%)'
    },
    {
      icon: '📢',
      title: 'Advertising',
      description: 'Strategic campaigns that capture attention and drive results. We create ads that inspire action.',
      gradient: 'linear-gradient(135deg, #4e5fce 0%, #7080d8 100%)'
    },
    {
      icon: '✏️',
      title: 'Design',
      description: 'Visual storytelling that brings your brand to life. We design experiences that leave lasting impressions.',
      gradient: 'linear-gradient(135deg, #ff4c4c 0%, #4e5fce 100%)'
    },
    {
      icon: '🌐',
      title: 'Digital Marketing',
      description: 'Data-driven strategies that maximize your online presence. SEO, social media, content marketing.',
      gradient: 'linear-gradient(135deg, #4e5fce 0%, #ff4c4c 100%)'
    },
    {
      icon: '🎬',
      title: 'Filming',
      description: 'Cinematic storytelling that engages and inspires. From concept to post-production.',
      gradient: 'linear-gradient(135deg, #ff4c4c 0%, #ffd1a4 100%)'
    },
    {
      icon: '💻',
      title: 'Website Development',
      description: 'Modern, responsive websites that deliver exceptional user experiences. Built for performance.',
      gradient: 'linear-gradient(135deg, #ffd1a4 0%, #4e5fce 100%)'
    },
    {
      icon: '📱',
      title: 'Mobile Apps',
      description: 'Innovative mobile solutions for iOS and Android. We build apps that users love.',
      gradient: 'linear-gradient(135deg, #4e5fce 0%, #ffd1a4 100%)'
    }
  ];

  // Get hero stats from translations (if you want to add them)
  const heroStats = t('hero.stats', { returnObjects: true }) || [
    { number: '150+', label: 'Projects Delivered' },
    { number: '50+', label: 'Happy Clients' },
    { number: '95%', label: 'Success Rate' },
    { number: '24/7', label: 'Support' }
  ];

  return (
    <div className="app">
      <ToastContainer
        position={i18n.language === 'ar' ? 'top-left' : 'top-right'}
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={i18n.language === 'ar'}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
      
      <Header />

      {/* Hero Section */}
      <section id="home" className="hero">
        <div className="hero-bg">
          <motion.div
            className="floating-shape shape-1"
            animate={{
              y: [0, -30, 0],
              rotate: [0, 180, 360]
            }}
            transition={{
              duration: 20,
              repeat: Infinity,
              ease: 'linear'
            }}
          />
          <motion.div
            className="floating-shape shape-2"
            animate={{
              y: [0, 40, 0],
              rotate: [0, -180, -360]
            }}
            transition={{
              duration: 25,
              repeat: Infinity,
              ease: 'linear'
            }}
          />
        </div>

        <div className="hero-content">
          <motion.div
            className="hero-tag"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            🚀 {t('hero.tagline')}
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            {t('hero.title')}
            <br />
            <span className="gradient-text">{t('hero.titleHighlight')}</span>
          </motion.h1>

          <motion.p
            className="hero-description"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
          >
            {t('hero.subtitle')}
          </motion.p>

          <motion.div
            className="hero-buttons"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.8 }}
          >
            <motion.a
              href="#services"
              className="btn-primary"
              whileHover={{ scale: 1.05, boxShadow: '0 20px 60px rgba(255, 76, 76, 0.4)' }}
              whileTap={{ scale: 0.95 }}
            >
              {t('hero.servicesButton', { defaultValue: 'Explore Services' })}
            </motion.a>
            <motion.a
              href="#contact"
              className="btn-secondary"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              {t('hero.cta', { defaultValue: 'Start a Project' })}
            </motion.a>
          </motion.div>

          <motion.div
            className="scroll-indicator"
            style={{ opacity }}
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 1.5, repeat: Infinity }}
          >
            <div className="mouse">
              <div className="wheel" />
            </div>
            <span>{t('hero.scrollText', { defaultValue: 'Scroll to explore' })}</span>
          </motion.div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="services">
        <div className="container">
          <motion.div
            className="section-header"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="section-tag">{t('services.tag')}</div>
            <h2>
              {t('services.title')}{' '}
              <span className="gradient-text">{t('services.titleHighlight')}</span>
            </h2>
            <p>{t('services.subtitle')}</p>
          </motion.div>

          <div className="services-grid">
            {services.map((service, index) => (
              <motion.div
                key={index}
                className="service-card"
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ y: -10, scale: 1.02 }}
              >
                <div className="service-glow" style={{ background: service.gradient }} />
              <div className="service-icon">{service.icon}</div>
                <h3>{service.title}</h3>
                <p>{service.description}</p>
                {/* <motion.div
                  className="service-arrow"
                  whileHover={{ x: i18n.language === 'ar' ? -5 : 5 }}
                >
                  {i18n.language === 'ar' ? '←' : '→'}
                </motion.div> */}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* About Section */}
      <About />

      {/* Portfolio Section */}
      <Portfolio />

      {/* Contact Section */}
      <Contact />

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-brand">
             {/* Logo */}
                <motion.a
                  href="#home"
                  className="logo"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setIsMenuOpen(false)}
                >
                  <img 
                    src="https://res.cloudinary.com/dzg4ssjwz/image/upload/v1768336636/logo_final_rouge_png_muyxcw.png
        
        
        " 
                    alt="Orvin Creative Agency" 
                    className="logo-image"
                  />
                </motion.a>
              <p>{t('footer.tagline')}</p>
              <div className="social-links">
                <motion.a 
                  href="https://www.facebook.com/orvin.creative.agency" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  whileHover={{ scale: 1.2, rotate: 5 }}
                >
                  <svg viewBox="0 0 24 24" fill="currentColor">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                </motion.a>
                <motion.a 
                  href="https://www.instagram.com/orvin.creative.agency/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  whileHover={{ scale: 1.2, rotate: 5 }}
                >
                  <svg viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073z"/>
                    <path d="M12 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                  </svg>
                </motion.a>
              </div>
            </div>

            <div className="footer-links">
              <h4>{t('footer.services', { defaultValue: 'Services' })}</h4>
              <ul>
                <li><a href="#services">{t('services.items.0.title') || 'Branding'}</a></li>
                <li><a href="#services">{t('services.items.1.title') || 'Advertising'}</a></li>
                <li><a href="#services">{t('services.items.2.title') || 'Design'}</a></li>
                <li><a href="#services">{t('services.items.3.title') || 'Digital Marketing'}</a></li>
                <li><a href="#services">{t('services.items.4.title') || 'Filming'}</a></li>
                <li><a href="#services">{t('services.items.5.title') || 'Web Development'}</a></li>
                <li><a href="#services">{t('services.items.6.title') || 'Mobile Apps'}</a></li>
              </ul>
            </div>

            <div className="footer-links">
              <h4>{t('footer.quickLinks', { defaultValue: 'Company' })}</h4>
              <ul>
                <li><a href="#about">{t('nav.about')}</a></li>
                <li><a href="#portfolio">{t('nav.portfolio')}</a></li>
                <li><a href="#contact">{t('footer.careers', { defaultValue: 'Careers' })}</a></li>
                <li><a href="#contact">{t('nav.contact')}</a></li>
              </ul>
            </div>

            <div className="footer-links">
              <h4>{t('footer.legal', { defaultValue: 'Legal' })}</h4>
              <ul>
                <li><a href="#">{t('footer.privacyPolicy', { defaultValue: 'Privacy Policy' })}</a></li>
                <li><a href="#">{t('footer.termsOfService', { defaultValue: 'Terms of Service' })}</a></li>
                <li><a href="#">{t('footer.cookiePolicy', { defaultValue: 'Cookie Policy' })}</a></li>
              </ul>
            </div>
          </div>

          <div className="footer-bottom">
            <p>
         {t('footer.copyright', { defaultValue: 'All rights reserved.' })} {t('footer.craftedWith', { defaultValue: 'Crafted with passion' })} 🔥
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;